import { workspaceFetch } from '../../platform/business-workspaces';
/**
 * The live adapter for the synthetic 06/08 management chain.
 * SYNTHETIC DATA / NON-OPERATIONAL.
 *
 * WHY IT REFUSES ANY ORIGIN THAT IS NOT A ROOT LOOPBACK ADDRESS. This slice is a synthetic
 * management write surface. It exists to be exercised from a developer's own machine against a
 * developer's own host, and it is not authorized to be reachable from, or to reach, anything else.
 * Enforcing that in the client means a misconfigured `baseOrigin` — a public address, a host with a
 * path, a URL carrying credentials — fails at construction rather than silently sending a session
 * token somewhere it should not go.
 *
 * WHAT IT NEVER DOES.
 *   - It never puts the session in a URL. A credential in a query string lands in access logs,
 *     proxy logs and browser history; it travels in a header.
 *   - It never retries. A retry with a fresh idempotency key would be a NEW effect, and a retry with
 *     the same key is the caller's decision to make, not the transport's.
 *   - It never turns a transport failure into a business outcome. No response means UNREACHABLE; a
 *     response it cannot parse means CONTRACT_DEFECT. Neither is ever reported as a refusal or a
 *     success, because each of those would be a claim about the server that this client cannot
 *     support.
 *   - It never sends credentials (`credentials: 'omit'`) and never follows a redirect
 *     (`redirect: 'error'`): a redirect is how a request ends up at an origin the caller did not
 *     choose.
 */
import type {
  ExactReadbackView,
  ProductModelView,
  ProductReviewPort,
  PublicationView,
  ReviewCaseView,
  RevisionRefInput,
  ServerOutcome,
  SubmissionView,
} from './product-review-port';
import {
  parseExactReadback,
  parseProductModel,
  parsePublicationEnvelope,
  parseReviewCase,
  parseReviewCaseEnvelope,
  parseServerError,
  parseSubmissionEnvelope,
} from './product-review-wire';

export const SESSION_HEADER = 'X-Rescue-Synthetic-Session';
export const CSRF_HEADER = 'X-Rescue-CSRF';
export const IDEMPOTENCY_HEADER = 'Idempotency-Key';

const MANAGEMENT_ROOT = '/management/product-review';

interface LiveProductReviewOptions {
  readonly baseOrigin: string;
  readonly fetchImpl?: typeof fetch;
  readonly csrf?: () => string;
}

function requireRootLoopbackOrigin(baseOrigin: string): string {
  // Check the supplied path BEFORE URL normalization can erase dot segments.
  if (!/^https?:\/\/[^/?#\\\s@]+\/?$/i.test(baseOrigin)) {
    throw new Error('A root local HTTP origin is required');
  }
  const url = new URL(baseOrigin);
  const ipv4 = url.hostname.split('.');
  const loopback =
    url.hostname === 'localhost' ||
    url.hostname === '[::1]' ||
    (ipv4.length === 4 &&
      ipv4[0] === '127' &&
      ipv4.every((part) => /^\d+$/.test(part) && Number(part) <= 255));
  if (!loopback || url.username || url.password || url.search || url.hash || url.pathname !== '/') {
    throw new Error('A root local HTTP origin is required');
  }
  return url.origin;
}

function revisionBody(revision: RevisionRefInput): Record<string, unknown> {
  const body: Record<string, unknown> = {
    productId: revision.productId,
    revision: revision.revision,
    digest: revision.digest,
    country: revision.country,
    supplier: revision.supplier,
    reviewScope: revision.reviewScope,
  };
  // Sent only when the caller set it, so the 06/08 product wire is unchanged for every consumer that
  // does not review a local asset.
  if (revision.subjectKind !== undefined) body['subjectKind'] = revision.subjectKind;
  return body;
}

function parseReviewList(body: unknown): readonly ReviewCaseView[] {
  if (
    typeof body !== 'object' ||
    body === null ||
    !('reviewCases' in body) ||
    Object.keys(body).length !== 1 ||
    !Array.isArray(body.reviewCases)
  ) {
    throw new Error('Invalid review case list');
  }
  return body.reviewCases.map(parseReviewCase);
}

export function createLiveProductReviewAdapter({
  baseOrigin,
  fetchImpl,
  csrf,
}: LiveProductReviewOptions): ProductReviewPort {
  const origin = requireRootLoopbackOrigin(baseOrigin);
  const send = fetchImpl ?? workspaceFetch;

  /**
   * One request, one outcome. `parse` is only ever called for a 2xx response, so a refusal body is
   * never fed to a success parser and a success body is never fed to the error parser.
   */
  async function call<T>(
    method: 'GET' | 'POST',
    path: string,
    session: string,
    parse: (body: unknown) => T,
    idempotencyKey?: string,
    body?: Record<string, unknown>,
  ): Promise<ServerOutcome<T>> {
    const headers: Record<string, string> = { [SESSION_HEADER]: session };
    if (method === 'POST' && csrf) headers[CSRF_HEADER] = csrf();
    if (idempotencyKey !== undefined) headers[IDEMPOTENCY_HEADER] = idempotencyKey;
    if (body !== undefined) headers['Content-Type'] = 'application/json';

    let response: Response;
    try {
      // The body is attached only when there is one. This is not a style choice: the project sets
      // `exactOptionalPropertyTypes`, so `body: undefined` is not the same as an absent body, and a
      // GET carrying an explicit `undefined` body is a different request from a GET with none.
      const init: RequestInit = {
        method,
        headers,
        credentials: 'omit',
        redirect: 'error',
        cache: 'no-store',
      };
      if (body !== undefined) init.body = JSON.stringify(body);
      response = await send(`${origin}${path}`, init);
    } catch (failure) {
      return {
        kind: 'UNREACHABLE',
        detail: failure instanceof Error ? failure.message : 'the request could not be sent',
      };
    }

    let parsedBody: unknown = null;
    let bodyIsJson = false;
    try {
      const raw = await response.text();
      if (raw.length > 0) {
        parsedBody = JSON.parse(raw);
        bodyIsJson = true;
      }
    } catch {
      bodyIsJson = false;
    }

    if (!response.ok) {
      if (!bodyIsJson) {
        return {
          kind: 'CONTRACT_DEFECT',
          detail: `HTTP ${response.status} with a body this client cannot read`,
        };
      }
      try {
        const error = parseServerError(parsedBody);
        if (error.code === 'UPSTREAM_OUTCOME_UNKNOWN' || error.code === 'PERSISTENCE_UNAVAILABLE') {
          return { kind: 'UNREACHABLE', detail: `HTTP ${response.status}: 写入结果未确认` };
        }
        return {
          kind: 'REFUSED',
          httpStatus: response.status,
          code: error.code,
          field: error.field,
        };
      } catch (defect) {
        return {
          kind: 'CONTRACT_DEFECT',
          detail: defect instanceof Error ? defect.message : 'unreadable error body',
        };
      }
    }

    if (!bodyIsJson) {
      return { kind: 'CONTRACT_DEFECT', detail: 'a successful response carried no JSON body' };
    }
    try {
      return { kind: 'OK', value: parse(parsedBody) };
    } catch (defect) {
      return {
        kind: 'CONTRACT_DEFECT',
        detail: defect instanceof Error ? defect.message : 'unreadable response body',
      };
    }
  }

  return {
    listCases(input) {
      return call('GET', `${MANAGEMENT_ROOT}/review-cases`, input.session, parseReviewList);
    },

    getProductReviews(input) {
      return call(
        'GET',
        `${MANAGEMENT_ROOT}/product-models/${encodeURIComponent(input.productId)}/reviews`,
        input.session,
        parseReviewList,
      );
    },

    resume(input) {
      return call<ProductModelView>(
        'POST',
        `${MANAGEMENT_ROOT}/product-models/${encodeURIComponent(input.productId)}/resumptions`,
        input.session,
        parseProductModel,
        input.idempotencyKey,
        { expectedVersion: input.expectedVersion, decisionId: input.decisionId },
      );
    },
    createDraft(input) {
      return call<ProductModelView>(
        'POST',
        `${MANAGEMENT_ROOT}/product-models`,
        input.session,
        parseProductModel,
        input.idempotencyKey,
        {
          productId: input.productId,
          country: input.country,
          supplier: input.supplier,
          claims: input.claims,
        },
      );
    },

    read(input) {
      return call<ProductModelView>(
        'GET',
        `${MANAGEMENT_ROOT}/product-models/${encodeURIComponent(input.productId)}`,
        input.session,
        parseProductModel,
      );
    },

    revise(input) {
      return call<ProductModelView>(
        'POST',
        `${MANAGEMENT_ROOT}/product-models/${encodeURIComponent(input.productId)}/revisions`,
        input.session,
        parseProductModel,
        input.idempotencyKey,
        { expectedVersion: input.expectedVersion, claims: input.claims },
      );
    },

    submit(input) {
      return call<SubmissionView>(
        'POST',
        `${MANAGEMENT_ROOT}/product-models/${encodeURIComponent(input.productId)}/submissions`,
        input.session,
        parseSubmissionEnvelope,
        input.idempotencyKey,
        { expectedVersion: input.expectedVersion },
      );
    },

    publish(input) {
      return call<PublicationView>(
        'POST',
        `${MANAGEMENT_ROOT}/product-models/${encodeURIComponent(input.submission.productId)}/publications`,
        input.session,
        parsePublicationEnvelope,
        input.idempotencyKey,
        { submission: revisionBody(input.submission), decisionId: input.decisionId },
      );
    },

    readExact(input) {
      const query = new URLSearchParams({
        country: input.country,
        supplier: input.supplier,
        digest: input.digest,
      });
      return call<ExactReadbackView>(
        'GET',
        `${MANAGEMENT_ROOT}/product-models/${encodeURIComponent(input.productId)}` +
          `/published/${input.publishedVersion}?${query.toString()}`,
        input.session,
        parseExactReadback,
      );
    },

    receive(input) {
      return call<ReviewCaseView>(
        'POST',
        `${MANAGEMENT_ROOT}/review-cases`,
        input.session,
        parseReviewCase,
        input.idempotencyKey,
        { caseId: input.caseId, revision: revisionBody(input.revision) },
      );
    },

    decide(input) {
      return call<ReviewCaseView>(
        'POST',
        `${MANAGEMENT_ROOT}/review-cases/${encodeURIComponent(input.caseId)}/decisions`,
        input.session,
        parseReviewCase,
        input.idempotencyKey,
        {
          revision: revisionBody(input.revision),
          expectedVersion: input.expectedVersion,
          decisionId: input.decisionId,
          outcome: input.outcome,
          reason: input.reason,
        },
      );
    },

    observe(input) {
      return call<ReviewCaseView>(
        'POST',
        `${MANAGEMENT_ROOT}/review-cases/${encodeURIComponent(input.caseId)}/observations`,
        input.session,
        parseReviewCase,
        input.idempotencyKey,
        { revision: revisionBody(input.revision), expectedVersion: input.expectedVersion },
      );
    },

    readCase(input) {
      // The reference travels as a QUERY here, not a body, because the route is a GET. That is the
      // only reason this one is spelled out rather than sharing the body builder above.
      const query = new URLSearchParams({
        productId: input.productId,
        revision: String(input.revision),
        digest: input.digest,
        country: input.country,
        supplier: input.supplier,
        reviewScope: input.reviewScope,
      });
      return call<ReviewCaseView>(
        'GET',
        `${MANAGEMENT_ROOT}/review-cases/${encodeURIComponent(input.caseId)}?${query.toString()}`,
        input.session,
        parseReviewCaseEnvelope,
      );
    },
  };
}
